import React from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { SiteLayout } from './layouts/SiteLayout.jsx';
import { VimPayLayout } from './layouts/VimPayLayout.jsx';
import { OpenAIAdsMeasurement } from './OpenAIAdsMeasurement.jsx';
import { syncOpenAIMeasurementConsent } from '../services/ads/openaiMeasurement.js';
import { SeoMetadata } from './SeoMetadata.jsx';
import { useSession } from './providers/SessionProvider.jsx';
import { supabase } from '../services/supabase/client.js';
import { FoundationPage } from '../pages/FoundationPage.jsx';
import {
  CatalogGridPage,
  GenreCatalogPage,
  HomeCatalogPage,
  SearchCatalogPage,
  SeriesDetailPage,
} from '../pages/CatalogPages.jsx';
import { ContinueWatchingPage } from '../pages/ContinueWatchingPage.jsx';
import { TvActivationPage } from '../pages/TvActivationPage.jsx';
import { TvParentalPage } from '../pages/TvParentalPage.jsx';
import { EpisodeDetailPlaybackEntryPage, TitleDetailPlaybackEntryPage } from '../pages/PlaybackEntryPages.jsx';
import { EpisodePlaybackPage, TitlePlaybackPage } from '../pages/PlaybackPage.jsx';
import {
  BillingPage,
  EditProfilePage,
  ForgotPasswordPage,
  LoginPage,
  MembershipPage,
  ProfilePage,
  RentalsPage,
  ResetPasswordPage,
  SignupPage,
  VerifyEmailPage,
} from '../pages/AccountPages.jsx';
import { MembershipCheckoutPage, TvodCheckoutPage } from '../pages/CommercePages.jsx';
import {
  CareerApplyPage,
  CareerDetailPage,
  CareersPage,
  CastingApplyPage,
  CastingNoticePage,
  CastingPage,
  SubmissionFormPage,
  SubmissionHubPage,
} from '../pages/BusinessPublicPages.jsx';
import { AdvertisePage } from '../pages/AdvertisingPages.jsx';
import {
  DisplayAdvertisingCheckoutPage,
  VideoAdvertisingCheckoutPage,
} from '../pages/AdvertisingCheckoutPages.jsx';
import {
  AdvertisingOverviewPage,
  AdvertisingPricingPage,
} from '../pages/AdvertisingLandingPages.jsx';
import { AdvertiserCampaignDashboardPage } from '../pages/AdvertiserCampaignDashboardPage.jsx';
import { FilmmakerPortalPage } from '../pages/FilmmakerPages.jsx';
import { FilmmakerOrdersPage } from '../pages/FilmmakerOrdersPage.jsx';
import { SupportPage, TermsPage } from '../pages/LegalPages.jsx';
import { CookiePolicyPage, PrivacyPage } from '../pages/PrivacyCookiePages.jsx';
import {
  AboutPage,
  AccessibilityPage,
  DistributionPage,
  DmcaPage,
  FaqPage,
  PartnersPage,
  PressPage,
  RefundPolicyPage,
} from '../pages/PublicInfoPages.jsx';
import { SubscriberCampaignPage } from '../pages/SubscriberCampaignPage.jsx';
import { VimPayAuthCallbackPage, VimPayDashboardPage, VimPayLoginPage, VimPayOnboardingPage } from '../pages/VimPayPages.jsx';
import { VimPayProjectsPage } from '../pages/VimPayProjectPages.jsx';
import { VimPayPaymentsPage } from '../pages/VimPayPaymentPages.jsx';
import { VimPayTaxPage } from '../pages/VimPayTaxPage.jsx';

const baseUrl = import.meta.env.BASE_URL || '/';
const routerBase = baseUrl === '/' ? undefined : baseUrl.replace(/\/$/, '');
const legacyHomeTabs = new Map([
  ['movies', '/movies'],
  ['shows', '/shows'],
  ['series', '/shows'],
  ['shorts', '/shorts'],
  ['originals', '/originals'],
]);

function searchSuffix(params, remove = []) {
  const next = new URLSearchParams(params);
  remove.forEach((key) => next.delete(key));
  const value = next.toString();
  return value ? `?${value}` : '';
}

function RouteMeasurement() {
  const location = useLocation();
  const protectedRoute = location.pathname.toLowerCase().replace(/\/+$/, '') === '/tv/parental';
  React.useLayoutEffect(() => {
    if (protectedRoute) syncOpenAIMeasurementConsent(false).catch(() => {});
  }, [protectedRoute]);
  // Never load campaign measurement on the PIN approval page.
  return protectedRoute ? null : <OpenAIAdsMeasurement />;
}

function HomeRoute() {
  const [params] = useSearchParams();
  const tab = String(params.get('tab') || '').trim().toLowerCase();
  return legacyHomeTabs.has(tab) ? <Navigate to={legacyHomeTabs.get(tab)} replace /> : <HomeCatalogPage />;
}

function LoginRoute() {
  const [params] = useSearchParams();
  const mode = String(params.get('mode') || 'login').trim().toLowerCase();
  const suffix = searchSuffix(params, ['mode']);
  if (mode === 'signup' || mode === 'register') return <Navigate to={`/signup${suffix}`} replace />;
  if (mode === 'reset' || mode === 'forgot') return <Navigate to={`/forgot-password${suffix}`} replace />;
  return <LoginPage />;
}

function PasswordRecoveryRouteGuard() {
  const { ready, user, recoveryMode } = useSession();
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!ready || !user || !recoveryMode || location.pathname === '/reset-password') return;
    navigate('/reset-password', { replace: true });
  }, [ready, user, recoveryMode, location.pathname, navigate]);

  return null;
}

function LegacyVimPayInviteRouteGuard() {
  const { ready, user } = useSession();
  const location = useLocation();
  const redirecting = React.useRef(false);
  const invited = user?.user_metadata?.vim_pay_invited === true;

  React.useEffect(() => {
    if (!ready || !user || !invited || redirecting.current) return;
    if (!['/', '/home'].includes(location.pathname)) return;

    redirecting.current = true;
    const clearInviteMarker = supabase
      ? supabase.auth.updateUser({
          data: {
            vim_pay_invited: false,
            vim_pay_invite_consumed_at: new Date().toISOString(),
          },
        }).catch(() => null)
      : Promise.resolve();

    clearInviteMarker.finally(() => {
      window.location.replace('/pay/onboarding');
    });
  }, [ready, user?.id, invited, location.pathname]);

  return null;
}

function LegacyCheckoutRoute() {
  const [params] = useSearchParams();
  const mode = String(params.get('mode') || '').trim().toLowerCase();
  const titleId = params.get('titleId') || params.get('title_id') || params.get('episodeId') || params.get('episode_id');
  const suffix = searchSuffix(params);
  if (titleId && !['membership', 'subscription'].includes(mode)) {
    return <Navigate to={`/checkout/${encodeURIComponent(titleId)}${suffix}`} replace />;
  }
  return <Navigate to={`/checkout/membership${suffix}`} replace />;
}

function LegacyDocumentRedirect({ to }) {
  React.useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return <FoundationPage title="Opening WatchVIM…" />;
}

function normalizeLegacyHashLocation() {
  if (typeof window === 'undefined') return;
  const hash = window.location.hash || '';
  if (!hash.startsWith('#/')) return;

  const legacyPath = hash.slice(1);
  const canonicalPath = routerBase ? `${routerBase}${legacyPath}` : legacyPath;
  window.history.replaceState(window.history.state, '', canonicalPath);
}

export function AppRouter() {
  normalizeLegacyHashLocation();

  return (
    <BrowserRouter basename={routerBase}>
      <SeoMetadata />
      <PasswordRecoveryRouteGuard />
      <LegacyVimPayInviteRouteGuard />
      <RouteMeasurement />
      <Routes>
        <Route path="/tv/parental" element={<TvParentalPage />} />
        <Route path="/subscribe" element={<SubscriberCampaignPage />} />

        <Route path="/pay" element={<VimPayLayout />}>
          <Route index element={<VimPayDashboardPage />} />
          <Route path="auth" element={<VimPayAuthCallbackPage />} />
          <Route path="login" element={<VimPayLoginPage />} />
          <Route path="onboarding" element={<VimPayOnboardingPage />} />
          <Route path="tax" element={<VimPayTaxPage />} />
          <Route path="projects" element={<VimPayProjectsPage />} />
          <Route path="payments" element={<VimPayPaymentsPage />} />
        </Route>

        <Route element={<SiteLayout />}>
          <Route path="/" element={<Navigate to="/home" replace />} />
          <Route path="/home" element={<HomeRoute />} />
          <Route path="/landing" element={<Navigate to="/home" replace />} />
          <Route path="/movies" element={<CatalogGridPage kind="movies" title="Movies" />} />
          <Route path="/shows" element={<CatalogGridPage kind="shows" title="Shows" />} />
          <Route path="/originals" element={<CatalogGridPage kind="originals" title="Originals" />} />
          <Route path="/shorts" element={<CatalogGridPage kind="shorts" title="Shorts" />} />
          <Route path="/search" element={<SearchCatalogPage />} />
          <Route path="/genres/:genre" element={<GenreCatalogPage />} />
          <Route path="/continue-watching" element={<ContinueWatchingPage />} />
          <Route path="/title/:titleId" element={<TitleDetailPlaybackEntryPage />} />
          <Route path="/series/:seriesId" element={<SeriesDetailPage />} />
          <Route path="/episode/:seriesId/:season/:episode" element={<EpisodeDetailPlaybackEntryPage />} />
          <Route path="/watch/:titleId" element={<TitlePlaybackPage />} />
          <Route path="/trailer/:titleId" element={<TitlePlaybackPage kind="trailer" />} />
          <Route path="/watch/:seriesId/season/:season/episode/:episode" element={<EpisodePlaybackPage />} />

          <Route path="/login" element={<LoginRoute />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/profile/edit" element={<EditProfilePage />} />
          <Route path="/profile/membership" element={<MembershipPage />} />
          <Route path="/profile/rentals" element={<RentalsPage />} />
          <Route path="/profile/billing" element={<BillingPage />} />
          <Route path="/profile/history" element={<Navigate to="/continue-watching" replace />} />
          <Route path="/profile/devices" element={<Navigate to="/profile" replace />} />
          <Route path="/my-list" element={<Navigate to="/home" replace />} />
          <Route path="/membership" element={<MembershipPage />} />
          <Route path="/checkout/membership" element={<MembershipCheckoutPage />} />
          <Route path="/checkout" element={<LegacyCheckoutRoute />} />
          <Route path="/rent/:titleId" element={<TvodCheckoutPage />} />
          <Route path="/checkout/:titleId" element={<TvodCheckoutPage />} />

          <Route path="/tv/activate" element={<TvActivationPage />} />
          <Route path="/activate" element={<TvActivationPage />} />
          <Route path="/roku/activate" element={<TvActivationPage />} />
          <Route path="/tv-activate" element={<TvActivationPage />} />
          <Route path="/redeem-code" element={<LegacyDocumentRedirect to="/redeem-code.html" />} />

          <Route path="/about" element={<AboutPage />} />
          <Route path="/press" element={<PressPage />} />
          <Route path="/press/*" element={<PressPage />} />
          <Route path="/partners" element={<PartnersPage />} />
          <Route path="/distribution" element={<DistributionPage />} />
          <Route path="/contact" element={<SupportPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/refund-policy" element={<RefundPolicyPage />} />
          <Route path="/accessibility" element={<AccessibilityPage />} />
          <Route path="/dmca" element={<DmcaPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/support" element={<SupportPage />} />

          <Route path="/advertise" element={<AdvertisingOverviewPage />} />
          <Route path="/advertise/video" element={<VideoAdvertisingCheckoutPage />} />
          <Route path="/advertise/display" element={<DisplayAdvertisingCheckoutPage />} />
          <Route path="/advertise/sponsorships" element={<AdvertisePage section="sponsorships" />} />
          <Route path="/advertise/pricing" element={<AdvertisingPricingPage />} />
          <Route path="/advertise/dashboard" element={<AdvertiserCampaignDashboardPage />} />
          <Route path="/advertise/campaigns" element={<Navigate to="/advertise/dashboard" replace />} />
          <Route path="/advertise/reports" element={<Navigate to="/advertise/dashboard" replace />} />

          <Route path="/submit" element={<SubmissionHubPage />} />
          <Route path="/submit/film" element={<SubmissionFormPage contentType="film" />} />
          <Route path="/submit/series" element={<SubmissionFormPage contentType="series" />} />
          <Route path="/submit/short" element={<SubmissionFormPage contentType="short" />} />
          <Route path="/submit/documentary" element={<SubmissionFormPage contentType="documentary" />} />

          <Route path="/casting" element={<CastingPage />} />
          <Route path="/casting/notices" element={<CastingPage />} />
          <Route path="/casting/my-submissions" element={<Navigate to="/casting" replace />} />
          <Route path="/casting/:noticeId" element={<CastingNoticePage />} />
          <Route path="/casting/:noticeId/apply" element={<CastingApplyPage />} />

          <Route path="/careers" element={<CareersPage />} />
          <Route path="/careers/jobs" element={<CareersPage filter="jobs" />} />
          <Route path="/careers/internships" element={<CareersPage filter="internships" />} />
          <Route path="/careers/freelance" element={<CareersPage filter="freelance" />} />
          <Route path="/careers/:jobId" element={<CareerDetailPage />} />
          <Route path="/careers/:jobId/apply" element={<CareerApplyPage />} />

          <Route path="/filmmakers" element={<FilmmakerPortalPage />} />
          <Route path="/filmmakers/dashboard" element={<FilmmakerPortalPage />} />
          <Route path="/filmmakers/titles" element={<FilmmakerPortalPage section="titles" />} />
          <Route path="/filmmakers/uploads" element={<FilmmakerPortalPage section="uploads" />} />
          <Route path="/filmmakers/revenue" element={<FilmmakerPortalPage section="revenue" />} />
          <Route path="/filmmakers/payouts" element={<FilmmakerPortalPage section="payouts" />} />
          <Route path="/filmmakers/messages" element={<FilmmakerPortalPage section="messages" />} />
          <Route path="/filmmakers/orders" element={<FilmmakerOrdersPage />} />
          <Route path="/filmmakers/profile" element={<FilmmakerPortalPage section="profile" />} />

          <Route path="/series/:seriesId/season/:season" element={<SeriesDetailPage />} />
          <Route path="*" element={<Navigate to="/home" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

